import { useQuery, useTRPC, useTRPCClient } from "@luckymenu/api-client/react";
import {
	type CsvFile,
	type CsvReport,
	type ReportByCategory,
	type ReportByHour,
	type ReportByItem,
	type ReportByPayment,
	type ReportByStaff,
	type ReportSummary,
	type ReportTaxSummary,
	type ReportVoids,
	restaurantBusinessDate,
} from "@luckymenu/core";
import { keepPreviousData, type QueryFunction } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { presetRange, type RangePreset, type ReportRange, rangeProblem } from "../reports/view";
import { useCurrentMembership } from "./use-current";
import { useCan, usePlanIncludes } from "./use-navigation";

/** What each `staff.reports.*` query answers. */
export type ReportData = {
	summary: ReportSummary;
	byItem: ReportByItem;
	byCategory: ReportByCategory;
	byPayment: ReportByPayment;
	byStaff: ReportByStaff;
	byHour: ReportByHour;
	taxSummary: ReportTaxSummary;
	voids: ReportVoids;
};
export type ReportName = keyof ReportData;

/** The detail screens (P11-T06), in the order the Reports screen lists them. */
export const REPORT_DETAILS = [
	"byItem",
	"byCategory",
	"byPayment",
	"byStaff",
	"byHour",
	"taxSummary",
	"voids",
] as const satisfies readonly ReportName[];

/** The part of a report procedure's options proxy this hook uses. */
type ReportProcedure<T> = {
	queryOptions(input: ReportRange): { queryKey: readonly unknown[]; queryFn?: QueryFunction<T> };
};

/** The restaurant's business date now: "today" for the presets. */
export function useBusinessToday(): string {
	const restaurant = useCurrentMembership().data?.restaurant;
	return restaurantBusinessDate({ timezone: restaurant?.timezone ?? "Asia/Kolkata" }, new Date());
}

/**
 * The range picker (P11-T05): a preset (Today, Yesterday, Last 7 days, Last
 * 30 days, This month, Last month) or two dates of one's own. `range` is the
 * last good range, which the screen shows while a custom one is wrong
 * (`problem`: a date missing, the start after the end, over 366 days).
 */
export function useReportRange(initial?: { preset: RangePreset; from?: string; to?: string }) {
	const today = useBusinessToday();
	const [preset, setPreset] = useState<RangePreset>(initial?.preset ?? "today");
	const [custom, setCustom] = useState<ReportRange>(() =>
		initial?.from && initial?.to
			? { from: initial.from, to: initial.to }
			: { from: today, to: today },
	);
	const wanted = preset === "custom" ? custom : presetRange(preset, today);
	const problem = preset === "custom" ? rangeProblem(custom) : null;
	const [lastGood, setLastGood] = useState<ReportRange>(wanted);
	if (problem === null && (lastGood.from !== wanted.from || lastGood.to !== wanted.to)) {
		setLastGood(wanted);
	}
	return {
		preset,
		/** The custom dates as typed. */
		custom,
		/** The range the reports show. */
		range: problem === null ? wanted : lastGood,
		problem,
		today,
		choose: (next: RangePreset) => {
			if (next === "custom" && preset !== "custom") setCustom(wanted);
			setPreset(next);
		},
		setCustom: (patch: Partial<ReportRange>) => {
			setPreset("custom");
			setCustom((c) => ({ ...(preset === "custom" ? c : wanted), ...patch }));
		},
	};
}

/**
 * One report over a range (`staff.reports.<name>`), for owners and managers
 * on a plan with reports. The last answer stays on screen while the range
 * changes.
 */
export function useReport<N extends ReportName>(name: N, range: ReportRange) {
	const trpc = useTRPC();
	// Both hooks always run: `&&` would skip the second until the membership loads.
	const mayView = useCan("reports.view");
	const hasReports = usePlanIncludes("reports");
	const allowed = mayView && hasReports;
	// The proxy's options differ per procedure only in their output type, which ReportData names.
	const procedure = trpc.staff.reports[name] as unknown as ReportProcedure<ReportData[N]>;
	const query = useQuery({
		...procedure.queryOptions(range),
		enabled: allowed,
		placeholderData: keepPreviousData,
	});
	return {
		status: query.data
			? ("ready" as const)
			: query.isError
				? ("error" as const)
				: ("loading" as const),
		data: query.data ?? null,
		/** A newer range is being fetched; `data` is still the previous one. */
		refreshing: query.isFetching && query.isPlaceholderData,
		retry: () => void query.refetch(),
	};
}

/**
 * The Today card (P11-T05): today's sales, bills and average bill from
 * `summary`, and the top five items from `byItem`.
 */
export function useTodayReport() {
	const today = useBusinessToday();
	const range = useMemo(() => ({ from: today, to: today }), [today]);
	const summary = useReport("summary", range);
	const items = useReport("byItem", range);
	return {
		status:
			summary.status === "ready" && items.status !== "loading"
				? ("ready" as const)
				: summary.status === "error"
					? ("error" as const)
					: ("loading" as const),
		today,
		summary: summary.data,
		topItems: items.data?.rows.slice(0, 5) ?? [],
		retry: () => {
			summary.retry();
			items.retry();
		},
	};
}

/**
 * CSV export (P11-T04) of a report or of every bill: the file for the app to
 * save (web: a download; Android: a file and the share sheet). Null when it
 * failed (`failed`).
 */
export function useCsvExport() {
	const client = useTRPCClient();
	const [busy, setBusy] = useState<CsvReport | null>(null);
	const [failed, setFailed] = useState(false);
	const run = useCallback(
		async (report: CsvReport, range: ReportRange): Promise<CsvFile | null> => {
			setBusy(report);
			setFailed(false);
			try {
				return await client.staff.reports.exportCsv.query({ report, ...range });
			} catch {
				setFailed(true);
				return null;
			} finally {
				setBusy(null);
			}
		},
		[client],
	);
	return { run, busy, failed };
}
